import React from "react"
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography, { TypographyProps } from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import { motion } from 'framer-motion';
import LinesComponent from './LinesComponent';
import Theme from './Theme';
import PropTypes from "prop-types"
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';
import CircularProgress from '@mui/material/CircularProgress';
import * as Turbo from "@hotwired/turbo"
import OnboardingStepper from './OnboardingStepper';


const OnboardingPage = ({ currentCompany }) => {
  // Initialize state using currentCompany prop, with fallbacks
  const [companyName, setCompanyName] = React.useState(currentCompany?.name || '');
  const [currency, setCurrency] = React.useState(currentCompany?.currency || 'GBP'); // Default or fetch options
  const [inventoryMethod, setInventoryMethod] = React.useState(currentCompany?.inventory_methodology || 'FIFO'); // Default or fetch options
  const [companyAddress, setCompanyAddress] = React.useState(currentCompany?.address || '');
  const [incorporationCountry, setIncorporationCountry] = React.useState(currentCompany?.country || 'United Kingdom'); // Default or fetch options
  // Access metadata, using optional chaining and providing fallbacks
  const [financialYearStart, setFinancialYearStart] = React.useState(currentCompany?.metadata?.financial_year_start || ''); 
  const [financialYearEnd, setFinancialYearEnd] = React.useState(currentCompany?.metadata?.financial_year_end || ''); 
  // State for validation errors
  const [errors, setErrors] = React.useState({});
  const [isSaving, setIsSaving] = React.useState(false);

  // Helper function to get CSRF token from meta tag
  const getCsrfToken = () => {
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    return token;
  };

  // Client-side validation function
  const validateForm = () => {
    const newErrors = {};

    if (!companyName) newErrors.name = "Name is required.";
    else if (companyName.length < 2) newErrors.name = "Name must be at least 2 characters.";

    if (!companyAddress) newErrors.address = "Address is required.";
    else if (companyAddress.length < 5) newErrors.address = "Address must be at least 5 characters.";

    if (!currency) newErrors.currency = "Currency is required."; // Although dropdown should prevent this
    // else if (!['GBP', 'USD', 'EUR'].includes(currency)) newErrors.currency = "Invalid currency selected."; // Redundant with dropdown

    if (!inventoryMethod) newErrors.inventory_methodology = "Inventory methodology is required."; // Although dropdown should prevent this

    if (!incorporationCountry) newErrors.country = "Country is required.";
    else if (incorporationCountry.length < 2) newErrors.country = "Country must be at least 2 characters."; // Check if dropdown value needs validation

    if (!financialYearStart) newErrors.financial_year_start = "Financial year start is required.";
    if (!financialYearEnd) newErrors.financial_year_end = "Financial year end is required.";

    setErrors(newErrors);
    // Return true if form is valid (no errors)
    return Object.keys(newErrors).length === 0;
  };

  // handleSave function to call the internal API
  const handleSave = async () => {
    if (isSaving) return;
    if (!validateForm()) return;

    setIsSaving(true);
    setErrors({});

    const csrfToken = getCsrfToken();
    if (!currentCompany?.id || !csrfToken) {
      console.error("Missing Company ID or CSRF Token for save operation.");
      setIsSaving(false);
      return;
    }

    const payload = {
      company: {
        name: companyName,
        address: companyAddress,
        currency: currency,
        inventory_methodology: inventoryMethod,
        country: incorporationCountry,
        metadata: {
          financial_year_start: financialYearStart,
          financial_year_end: financialYearEnd,
        },
      },
    };

    try {
      const response = await fetch(`/api/internal/companies/${currentCompany.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        console.log("Company updated successfully");
        // Construct the next URL, preserving existing params and setting step=settings
        const currentSearchParams = new URLSearchParams(window.location.search);
        currentSearchParams.set('step', 'settings');
        const nextUrl = `/onboarding?${currentSearchParams.toString()}`;

        // Use Turbo to visit the new URL, triggering Rails controller action
        // and replacing the page body without full reload.
        // If using Turbolinks classic, use Turbolinks.visit(nextUrl) instead.
        Turbo.visit(nextUrl);
      } else {
        const errorData = await response.json();
        console.error("Failed to update company:", errorData);
        setErrors(parseBackendErrors(errorData.errors));
      }
    } catch (error) {
      console.error("API request failed:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const formProps = {
    companyName, currency, inventoryMethod, companyAddress, incorporationCountry,
    financialYearStart, financialYearEnd, 
    setCompanyName,
    setCurrency,
    setInventoryMethod,
    setCompanyAddress,
    setIncorporationCountry,
    setFinancialYearStart,
    setFinancialYearEnd,
    companyId: currentCompany?.id, // Pass company ID
    handleSave, // Pass the save handler
    errors, // Pass errors state
    isSaving,
  };

  const handleInputChange = (setter) => (event) => {
    setter(event.target.value);
  };

  return (
    <Theme>
    <React.Fragment>
       <Grid container component="main" sx={{ height: '100vh' }}>
        
        <Grid
          item
          size={5}
          sx={{
            background:
              'linear-gradient(211.21deg, #3376C2 29.32%, #2B62A1 95.41%)',
            position: 'relative',
            overflow: 'hidden',
            display: { xs: 'none', md: 'block' }
          }}
        >

          <Box
            position="absolute"
            top={108}
            left={59}
            component={motion.div}
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, type: 'spring', bounce: 0.25 }}
            zIndex={1}
          >
            <img src="/logo-large.png" alt="Contacryto" width={258} height={66} />
          <Typography
            variant="h4"
            mt={4}
            sx={{ color: 'common.white', maxWidth: '30rem' }}
          >
            Confirm your company information
          </Typography>
          <Typography
            variant="h5"
            mt={4}
            sx={{ color: 'common.white', maxWidth: '30rem' }}
          >
            We have imported the company information from your Xero account. Please indicate your inventory methodology and the starting fiscal year that you want to use ContaCrypto.
          </Typography>
          </Box>


          <Box
            position="absolute"
            right={0}
            bottom={-4}
            component={motion.div}
            initial={{ opacity: 0, scale: 0.9, x: -600, y: 300 }}
            animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
            transition={{ duration: 1, type: 'spring', bounce: 0.25 }}
          >
            {/* <LinesComponent /> */}
        </Box>
        </Grid>


        <Grid
          item
          sx={{
            height: '100vh',
            overflowY: 'auto',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '2rem',
            flexGrow: 1
          }}
        >
          <OnboardingStepper activeStep={1} />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              maxWidth: '500px',
              width: '100%'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                maxWidth: '500px',
                width: '100%',
                textAlign: 'left', // Align labels to the left
              }}
            >
              <Typography variant="h4" component="h1" gutterBottom sx={{ alignSelf: 'flex-start', mb: 1 }}>
                Contacrypto
              </Typography>
              <Typography variant="h5" component="h2" gutterBottom sx={{ alignSelf: 'flex-start', mb: 1 }}>
                Select Xero Accounts
              </Typography>
              <Typography variant="body1" gutterBottom sx={{ alignSelf: 'flex-start', mb: 3 }}>
                Instructions: Please use the dropdown menu to select the corresponding Xero account number. If you can't find the account, you will have to create it in Xero in the "Chart of accounts" page, then come back and refresh the page.
              </Typography>

              <Stack spacing={3} sx={{ width: '100%' }}>
                <TextField
                  required
                  fullWidth
                  id="company-name"
                  label="What is the name of your company?"
                  name="companyName"
                  defaultValue="Contacrypto" // Example pre-filled value
                  variant="standard"
                  value={companyName}
                  onChange={handleInputChange(setCompanyName)}
                  error={!!errors.name}
                  helperText={errors.name || ''}
                />
                <FormControl fullWidth required variant="standard" error={!!errors.currency}>
                  <InputLabel id="currency-label">What currency does your organization use?</InputLabel>
                  <Select
                    labelId="currency-label"
                    id="currency"
                    value={currency}
                    label="What currency does your organization use?"
                    onChange={handleInputChange(setCurrency)}
                  >
                    <MenuItem value={'GBP'}>GBP</MenuItem>
                    <MenuItem value={'USD'}>USD</MenuItem>
                    <MenuItem value={'EUR'}>EUR</MenuItem>
                  </Select>
                  {errors.currency && <FormHelperText error>{errors.currency}</FormHelperText>}
                </FormControl>
                <FormControl fullWidth required variant="standard" error={!!errors.inventory_methodology}>
                  <InputLabel id="inventory-methodology-label">Which inventory methodology does your company use?</InputLabel>
                  <Select
                    labelId="inventory-methodology-label"
                    id="inventory-methodology"
                    value={inventoryMethod}
                    label="Which inventory methodology does your company use?"
                    onChange={handleInputChange(setInventoryMethod)}
                  >
                    <MenuItem value={'FIFO'}>FIFO</MenuItem>
                  </Select>
                  {errors.inventory_methodology && <FormHelperText error>{errors.inventory_methodology}</FormHelperText>}
                </FormControl>
                <TextField
                  required
                  fullWidth
                  id="company-address"
                  label="What is the address of your company?"
                  name="companyAddress"
                  variant="standard"
                  value={companyAddress}
                  onChange={handleInputChange(setCompanyAddress)}
                  error={!!errors.address}
                  helperText={errors.address || ''}
                />
                <FormControl fullWidth required variant="standard" error={!!errors.country}>
                  <InputLabel id="incorporation-country-label">In which country is your company incorporated?</InputLabel>
                  <Select
                    labelId="incorporation-country-label"
                    id="incorporation-country"
                    value={incorporationCountry}
                    label="In which country is your company incorporated?"
                    onChange={handleInputChange(setIncorporationCountry)}
                  >
                    <MenuItem value={'United Kingdom'}>United Kingdom</MenuItem>
                    <MenuItem value={'United States'}>United States</MenuItem>
                  </Select>
                  {errors.country && <FormHelperText error>{errors.country}</FormHelperText>}
                </FormControl>
                <TextField
                  required
                  fullWidth
                  id="financial-year-start"
                  label="When does your financial year start?"
                  name="financialYearStart"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  variant="standard"
                  value={financialYearStart}
                  onChange={handleInputChange(setFinancialYearStart)}
                  error={!!errors.financial_year_start}
                  helperText={errors.financial_year_start || ''}
                />
                <TextField
                  required
                  fullWidth
                  id="financial-year-end"
                  label="When does your financial year end?"
                  name="financialYearEnd"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  variant="standard"
                  value={financialYearEnd}
                  onChange={handleInputChange(setFinancialYearEnd)}
                  error={!!errors.financial_year_end}
                  helperText={errors.financial_year_end || ''}
                />

                <Button
                  onClick={handleSave}
                  fullWidth
                  variant="contained"
                  disabled={isSaving}
                  sx={{ mt: 3, mb: 1, position: 'relative' }}
                >
                  {isSaving ? (
                    <CircularProgress 
                      size={24} 
                      sx={{ 
                        color: 'white', 
                      }}
                    />
                  ) : (
                    'Next' 
                  )}
                </Button>
                <Link href="#" variant="body2" sx={{ alignSelf: 'center', mb: 2 }}>
                  Skip
                </Link>
              </Stack>
            </Box>
          </Box>
        </Grid>

       </Grid>

    </React.Fragment>
    </Theme>
  )
}

OnboardingPage.propTypes = {
  currentCompany: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    address: PropTypes.string,
    currency: PropTypes.string,
    inventory_methodology: PropTypes.string,
    country: PropTypes.string,
    metadata: PropTypes.shape({
      financial_year_start: PropTypes.string, // Assuming dates are passed as strings
      financial_year_end: PropTypes.string
    })
  })
};

export default OnboardingPage
